import {
  Navigate,
  BrowserRouter,
  Outlet,
  Route,
  Routes,
} from "react-router-dom";

import { useLatestVersionChecker } from "@hooks";
import { RequestType } from "@interfaces/Request";
import { DocumentCreationSuccess, PDFViewer, ScrollToTop } from "@components";
import {
  DocumentCreationContextProvider,
  DocumentNotificationContextProvider,
  DocumentSignatureContextProvider,
  TemplateCreationProvider,
  PrescriptionFlowProvider,
  TemplateFlowProvider,
  TemplateEditProvider,
  TemplateEditFlowProvider,
} from "@contexts";

import { Login, VerifyAuthCode, UpdateContactScreen, BiometricRegistration } from "@screens/Auth";

import { RegistrationForm } from "@screens/Registration/RegistrationForm";
import {
  PrescriptionCreate,
  PrescriptionTreatment,
  AddMedicineManually,
  PrescriptionTreatmentEdit,
  TemplateSummary,
  TemplateEdit,
  TemplateDetailEdit,
} from "@screens/PrescriptionForm";

import {
  SearchProcedure,
  AddProcedureManually,
  MedicalOrderCreate,
} from "@screens/MedicalOrder";

import { Requests } from "@screens/Requests";
import {
  RequestManagement,
  SelectDocument,
} from "@screens/Requests/RequestManagement";
import { HistoricRequest } from "@screens/Requests/HistoricRequests/HistoricRequest";
import {
  CompoundedPrescriptionCreate,
  MedicalCertificateCreate,
  LandingPage,
  PersonalData,
  NotFound,
  SearchMedicines,
} from "@screens";
import {
  NewDocumentType,
  NewDocumentPatient,
  AddPatientInfo,
  EditPatientInfo,
} from "@screens/NewDocumentRequest";
import {
  DocumentCodeVerificationScreen,
  DocumentValidationScreen,
} from "@screens/MedicalDocsValidation";
import {
  PrescriptionCodeVerificationScreen,
  PrescriptionValidationScreen,
  QFDataFormScreen,
} from "@screens/PrescriptionValidation";
import {
  AddAddressScreen,
  CanceledBankTransferScreen,
  CreatePinScreen,
  IdentityVerification,
  SignRecipeScreen,
  SignRecipeScreenSecondFactor,
  StartBankTransferScreen,
  SuccessfulAuth,
  SuccessfulBankTransferScreen,
  ValidationChallengeScreen,
} from "@screens/FeaAuth";
import { PrivateRoute } from "./PrivateRoute";
import { PublicOnlyRoute } from "./PublicOnlyRoute";

export const AppRouter = () => {
  useLatestVersionChecker();

  return (
    <>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="" element={<PublicOnlyRoute />}>
            <Route path="" element={<LandingPage />} />
            <Route path="/app">
              <Route path="" element={<Login />} />
              <Route path="codigo" element={<VerifyAuthCode />} />
              <Route
                path="actualizar-contacto"
                element={<UpdateContactScreen />}
              />
              <Route path="registro" element={<RegistrationForm />} />
              <Route path="farmacia">
                <Route
                  path=""
                  element={<PrescriptionCodeVerificationScreen />}
                />
                <Route path=":prescriptionId">
                  <Route path="" element={<PrescriptionValidationScreen />} />
                  <Route path="info-retencion" element={<QFDataFormScreen />} />
                </Route>
              </Route>
              <Route path="verificacion/:documentType">
                <Route path="" element={<DocumentCodeVerificationScreen />} />
                <Route path=":requestId">
                  <Route path="" element={<DocumentValidationScreen />} />
                </Route>
              </Route>
              <Route path="visualizacion-documento" element={<PDFViewer />} />
            </Route>
          </Route>

          <Route path="/app/registro-biometrico" element={<PrivateRoute />}>
            <Route path="" element={<BiometricRegistration />} />
          </Route>

          <Route path="/app/verificacion-identidad" element={<PrivateRoute />}>
            <Route path="" element={<IdentityVerification />} />
          </Route>

          <Route path="/app/nuevo-documento" element={<PrivateRoute />}>
            <Route path="" element={<NewDocumentType />} />
            <Route path="paciente" element={<NewDocumentPatient />} />
            <Route path="paciente/agregar" element={<AddPatientInfo />} />
            <Route path="paciente/editar" element={<EditPatientInfo />} />
          </Route>

          <Route
            path="/app/solicitudes"
            element={
              <DocumentCreationContextProvider>
                <DocumentNotificationContextProvider>
                  <DocumentSignatureContextProvider>
                    <PrivateRoute />
                  </DocumentSignatureContextProvider>
                </DocumentNotificationContextProvider>
              </DocumentCreationContextProvider>
            }
          >
            <Route path="" element={<Requests />} />
            <Route path=":requestId">
              <Route path="gestionar">
                <Route path="" element={<RequestManagement />} />
                <Route
                  path="seleccionar-documento"
                  element={<SelectDocument />}
                />
              </Route>

              <Route path="receta">
                <Route path="crear" element={<PrescriptionCreate />} />
                <Route element={<PrescriptionFlowProvider />}>
                  <Route path="crear/buscar" element={<SearchMedicines />} />
                  <Route
                    path="crear/tratamiento"
                    element={<PrescriptionTreatment />}
                  />
                  <Route
                    path="crear/añadir-manual"
                    element={<AddMedicineManually />}
                  />
                </Route>
                <Route
                  path="editar/tratamiento/:treatmentId"
                  element={<PrescriptionTreatmentEdit />}
                />
                <Route
                  path="firma/factor-autenticacion"
                  element={<SignRecipeScreenSecondFactor />}
                />
                <Route path="firma" element={<SignRecipeScreen />} />
                <Route
                  path="exito"
                  element={
                    <DocumentCreationSuccess
                      requestType={RequestType.PRESCRIPTION}
                    />
                  }
                />
              </Route>
              <Route path="plantilla">
                <Route
                  element={
                    <TemplateCreationProvider>
                      <Outlet />
                    </TemplateCreationProvider>
                  }
                >
                  <Route path="crear" element={<TemplateSummary />} />
                  <Route element={<TemplateFlowProvider />}>
                    <Route
                      path="crear/tratamiento/buscar"
                      element={<SearchMedicines />}
                    />
                    <Route
                      path="crear/tratamiento"
                      element={<PrescriptionTreatment />}
                    />
                    <Route
                      path="crear/tratamiento/añadir-manual"
                      element={<AddMedicineManually />}
                    />
                  </Route>
                </Route>
                <Route element={<TemplateEditProvider />}>
                  <Route
                    path=":templateId/editar"
                    element={<TemplateEdit />}
                  />
                  <Route
                    path=":templateId/editar/detalle/:localId"
                    element={<TemplateDetailEdit />}
                  />
                  <Route element={<TemplateEditFlowProvider />}>
                    <Route
                      path=":templateId/editar/buscar"
                      element={<SearchMedicines />}
                    />
                    <Route
                      path=":templateId/editar/tratamiento"
                      element={<PrescriptionTreatment />}
                    />
                    <Route
                      path=":templateId/editar/añadir-manual"
                      element={<AddMedicineManually />}
                    />
                  </Route>
                </Route>
              </Route>
              <Route path="receta-magistral">
                <Route
                  path="crear"
                  element={<CompoundedPrescriptionCreate />}
                />
                <Route path="firma" element={<SignRecipeScreen />} />
                <Route
                  path="exito"
                  element={
                    <DocumentCreationSuccess
                      requestType={RequestType.COMPOUNDED_PRESCRIPTION}
                    />
                  }
                />
              </Route>
              <Route path="orden-medica">
                <Route path="crear" element={<MedicalOrderCreate />} />
                <Route path="crear/buscar" element={<SearchProcedure />} />
                <Route
                  path="crear/añadir-manual"
                  element={<AddProcedureManually />}
                />
                <Route
                  path="exito"
                  element={
                    <DocumentCreationSuccess
                      requestType={RequestType.MEDICAL_ORDER}
                    />
                  }
                />
              </Route>
              <Route path="certificado">
                <Route path="crear" element={<MedicalCertificateCreate />} />
                <Route
                  path="exito"
                  element={
                    <DocumentCreationSuccess
                      requestType={RequestType.MEDICAL_CERTIFICATE}
                    />
                  }
                />
              </Route>
              <Route path="resumen" element={<HistoricRequest />} />
            </Route>
            <Route path="visualizacion-documento" element={<PDFViewer />} />
          </Route>

          <Route
            path="/app/registro/firma-electronica-avanzada"
            element={<PrivateRoute />}
          >
            <Route
              path="validation-challenge"
              element={<ValidationChallengeScreen />}
            />
            <Route
              path="transferencia-bancaria"
              element={<StartBankTransferScreen />}
            />
            <Route
              path="transferencia-bancaria/cancelado"
              element={<CanceledBankTransferScreen />}
            />
            <Route
              path="transferencia-bancaria/exito"
              element={<SuccessfulBankTransferScreen />}
            />
            <Route path="crear-pin" element={<CreatePinScreen />} />
            <Route path="direccion" element={<AddAddressScreen />} />
            <Route path="exito" element={<SuccessfulAuth />} />
          </Route>

          <Route path="/app/datos-personales" element={<PrivateRoute />}>
            <Route path="" element={<PersonalData />} />
            <Route path="cambiar-direccion" element={<AddAddressScreen />} />
          </Route>

          <Route path="*" element={<Navigate to="/app/404" />} />

          <Route path="/app/404" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </>
  );
};
